import { useCallback, useEffect, useState } from 'react';
import type { AdminWhatsappCommsSettings } from '../../../domain/superadmin/types';
import { useAppContext } from '../../../contexts/AppContext';
import {
  fetchAdminWhatsappCommsSettings,
  updateAdminWhatsappCommsSettings,
} from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

export function useAdminWhatsappCommsSettings() {
  const [settings, setSettings] = useState<AdminWhatsappCommsSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminWhatsappCommsSettings();
      setSettings(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar configurações de WhatsApp');
    } finally {
      setIsLoading(false);
    }
  }, [handleForbidden]);

  const setCommsEnabled = useCallback(
    async (enabled: boolean) => {
      const prev = settings;
      if (prev) {
        setSettings({ ...prev, commsEnabled: enabled });
      }
      try {
        const updated = await updateAdminWhatsappCommsSettings(enabled);
        setSettings(updated);
      } catch (e) {
        setSettings(prev);
        if (e instanceof SuperadminApiError && e.isForbidden) {
          handleForbidden();
          return;
        }
        notify.error(e instanceof Error ? e.message : 'Falha ao atualizar configurações');
        await load();
      }
    },
    [settings, handleForbidden, load],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return { settings, isLoading, error, refetch: load, setCommsEnabled };
}
